// The playground's EXPLORER (the owner's VS Code picture, 2026-10-17): the batch's folders and files at any depth,
// new file / new folder / rename / delete, the sandbox's node_modules and .hub as closed folders, the generated tests/
// folder, and an OUTLINE of the open file: its endpoint (route, method, outcomes, test events) or what it is.
import { useState } from 'react'
import { ChevronDown, ChevronRight, ChevronsDownUp, FilePlus, FolderPlus, Pencil, Trash2 } from 'lucide-react'
import { endpointLabel, folderOf, routeOfFile } from '@/lib/endpoints'
import { isApiPath, readDefineApi } from '@/lib/manifest'
import type { TreeNode } from '@/lib/batch'
import type { Problem } from '@/lib/problems'
import type { CodeFile, Manifest, TestEvent } from '@/types'

export const DOT: Record<string, string> = { js: 'bg-amber-400', json: 'bg-violet-400', md: 'bg-emerald-500' }
export const dotOf = (path: string) => DOT[path.split('.').pop() ?? ''] ?? 'bg-slate-400'

export interface ExplorerProps {
  title: string
  tree: TreeNode[]
  active: string
  changed: (path: string) => 'A' | 'M' | null
  problems: Problem[]
  editable: boolean
  onOpen: (path: string, line?: number) => void
  onNewFile: (folder: string) => void
  onNewFolder: (folder: string) => void
  onRename: (path: string) => void
  onDelete: (path: string) => void
  /** outline */
  activeFile: CodeFile | undefined
  manifest: Manifest
  tests: TestEvent[]
}

export function Explorer(p: ExplorerProps) {
  const [closed, setClosed] = useState<Set<string>>(new Set())
  const toggle = (path: string) => setClosed((c) => { const n = new Set(c); if (n.has(path)) n.delete(path); else n.add(path); return n })
  const activeFolder = p.active && !p.active.startsWith('tests/') ? folderOf(p.active) : ''

  const row = (n: TreeNode, depth: number) => {
    const isFolder = n.kind === 'folder' || n.kind === 'tests-folder'
    const open = !closed.has(n.path)
    const mark = n.kind === 'file' ? p.changed(n.path) : null
    const errs = n.kind === 'file' ? p.problems.filter((x) => x.path === n.path && x.level === 'error').length : 0
    return (
      <li key={n.path}>
        <div
          className={`group flex h-[22px] items-center gap-1 pr-1 text-[12.5px] ${n.kind === 'closed' ? 'cursor-default text-muted' : 'cursor-pointer'} ${n.path === p.active ? 'bg-primary/15 text-default' : n.kind !== 'closed' ? 'hover:bg-slate-200/70 dark:hover:bg-slate-800' : ''}`}
          style={{ paddingLeft: 8 + depth * 12 }}
          onClick={() => (n.kind === 'closed' ? undefined : isFolder ? toggle(n.path) : p.onOpen(n.path))}
          role="treeitem"
          aria-expanded={isFolder ? open : undefined}
          aria-selected={n.path === p.active}
          data-testid={`tree-${n.path}`}
          title={n.kind === 'closed' ? 'in the sandbox only (not in git, not in the editor)' : n.kind === 'tests' ? 'generated from the test events; read-only here' : n.path}
        >
          {isFolder || n.kind === 'closed' ? (open && n.kind !== 'closed' ? <ChevronDown size={13} className="shrink-0 text-muted" /> : <ChevronRight size={13} className="shrink-0 text-muted" />) : <span className={`ml-1 mr-0.5 h-2 w-2 shrink-0 rounded-sm ${dotOf(n.path)}`} aria-hidden="true" />}
          <span className={`min-w-0 flex-1 truncate ${errs ? 'text-red-600 dark:text-red-400' : mark ? 'text-amber-700 dark:text-amber-300' : ''} ${n.kind === 'tests' || n.kind === 'tests-folder' || n.kind === 'closed' ? 'italic text-muted' : ''}`}>
            {n.name}
          </span>
          {p.editable && n.kind !== 'tests' && n.kind !== 'tests-folder' && n.kind !== 'closed' && (
            <span className="hidden shrink-0 items-center gap-0.5 group-hover:flex" onClick={(e) => e.stopPropagation()}>
              {n.kind === 'folder' && (
                <>
                  <button type="button" className="icon-btn !p-0.5" aria-label={`New file in ${n.path}`} title="New file here" onClick={() => p.onNewFile(n.path)}><FilePlus size={12} /></button>
                  <button type="button" className="icon-btn !p-0.5" aria-label={`New folder in ${n.path}`} title="New folder here" onClick={() => p.onNewFolder(n.path)}><FolderPlus size={12} /></button>
                </>
              )}
              {n.path !== 'package.json' && <button type="button" className="icon-btn !p-0.5" aria-label={`Rename ${n.path}`} title="Rename / move" onClick={() => p.onRename(n.path)}><Pencil size={11} /></button>}
              {n.path !== 'package.json' && <button type="button" className="icon-btn !p-0.5" aria-label={`Delete ${n.path}`} title="Delete" onClick={() => p.onDelete(n.path)}><Trash2 size={11} /></button>}
            </span>
          )}
          {errs > 0 && <span className="shrink-0 rounded-full bg-red-600 px-1 text-[10px] font-bold leading-4 text-white" title={`${errs} error(s)`}>{errs}</span>}
          {mark && <span className="w-3 shrink-0 text-center text-[10px] font-bold text-amber-600 dark:text-amber-300" title={mark === 'A' ? 'new, not saved' : 'changed, not saved'}>{mark}</span>}
        </div>
        {isFolder && open && n.children && <ul role="group">{n.children.map((c) => row(c, depth + 1))}</ul>}
      </li>
    )
  }

  const f = p.activeFile
  const route = f ? routeOfFile(f) : null
  const def = f && route ? readDefineApi(f.content) : null
  const ep = route ? p.manifest.endpoints.find((e) => e.path === route) : undefined
  const testCount = route ? p.tests.filter((t) => t.endpoint === route).length : 0

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="pg-explorer">
      <div className="flex h-9 shrink-0 items-center justify-between px-3 text-[11px] font-semibold uppercase tracking-wider text-muted">
        Explorer
        {p.editable && (
          <span className="flex items-center gap-0.5">
            <button type="button" className="icon-btn !p-1" aria-label="New file" title={`New file${activeFolder ? ` in ${activeFolder}/` : ''}`} onClick={() => p.onNewFile(activeFolder)}><FilePlus size={14} /></button>
            <button type="button" className="icon-btn !p-1" aria-label="New folder" title={`New folder${activeFolder ? ` in ${activeFolder}/` : ''}`} onClick={() => p.onNewFolder(activeFolder)}><FolderPlus size={14} /></button>
            <button type="button" className="icon-btn !p-1" aria-label="Collapse folders" title="Collapse folders" onClick={() => setClosed(new Set(p.tree.filter((n) => n.children).flatMap(function all(n: TreeNode): string[] { return [n.path, ...(n.children ?? []).filter((c) => c.children).flatMap(all)] })))}><ChevronsDownUp size={14} /></button>
          </span>
        )}
      </div>
      <div className="flex h-6 shrink-0 items-center gap-1 px-2 text-[11px] font-bold uppercase tracking-wide">
        <ChevronDown size={13} className="text-muted" /> <span className="truncate">{p.title}</span>
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto pb-2" role="tree" aria-label="Files">
        {p.tree.map((n) => row(n, 0))}
      </ul>
      <div className="max-h-[38%] shrink-0 overflow-y-auto border-t border-slate-200 dark:border-slate-800" data-testid="pg-outline">
        <div className="flex h-7 items-center px-3 text-[11px] font-semibold uppercase tracking-wider text-muted">
          Outline{f ? <span className="ml-1 font-normal normal-case tracking-normal">· {f.path}</span> : null}
        </div>
        <div className="px-4 pb-2 text-[12px]">
          {f && route && def && (
            <>
              <button type="button" className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-slate-200/70 dark:hover:bg-slate-800" onClick={() => p.onOpen(f.path, def.line)}>
                <span className="font-mono text-[11px] text-violet-600 dark:text-violet-300">{def.method}</span>
                <span className="min-w-0 flex-1 truncate font-medium">{route}</span>
                <span className="text-[10px] text-muted">L{def.line}</span>
              </button>
              <div className="mt-1 text-[11px] text-muted">
                {def.title ? <div>{def.title}</div> : null}
                <div>outcomes: {def.outcomes.join(' · ')}</div>
                <div>{testCount} test event{testCount === 1 ? '' : 's'} · {ep ? 'in the manifest' : 'not yet in the manifest: Run or Discover endpoints'}{def.tokenSource ? ' · token source' : ''}</div>
              </div>
            </>
          )}
          {f && isApiPath(f.path) && !def && <p className="text-[11px] text-muted">Under src/api/ but no endpoint yet: <code>module.exports = defineApi({'{'} outcomes, handler {'}'})</code>.</p>}
          {f && !isApiPath(f.path) && <p className="text-[11px] text-muted">{f.path === 'src/server.js' ? 'Starts the service (npm start; the live container runs this).' : f.path === 'src/app.js' ? 'Builds the service: createApp() mounts every endpoint under src/api; nothing is listed by hand.' : /.(c?js|mjs)$/.test(f.path) ? 'Helper code: require it from an endpoint file.' : f.path === 'package.json' ? 'The project: dependencies go here (npm install in the terminal).' : 'Not code.'}</p>}
          {!f && <p className="text-[11px] text-muted">Open a file to see its endpoint.</p>}
          {f && route && <p className="mt-1 text-[10px] text-muted">Label on the canvas: {endpointLabel(route)}</p>}
        </div>
      </div>
    </div>
  )
}
